import * as React from "react";
import { FileTypeEnum } from "../../enums/fileTypeEnum";
import { ModeEnum } from "../../enums/modeEnum";
import { FolderTypeIconEnum } from "../../enums/FolderTypeIconEnum";
import { FolderTypeIconQuery } from "../../helpers/folderTypeIconQuery";
import { IStoreItem } from "../../../interfaces/IStoredItem";
import { DialogType, FontIcon, Icon, IDialogContentProps, TooltipHost, TooltipOverflowMode, Text } from "@fluentui/react";
import { AccessLicenseHelper } from "../../helpers/accessLicenseHelper";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../redux/index";
import { setLoading } from "../../../redux/modules/loader";
import { setShowFolderDeleteDialog } from "../../../redux/modules/showFolderDeleteDialogue";
import { setWorkingDirectories } from "../../../redux/modules/workingDirectory";
import { ItemCheckFactory } from "../../helpers/ItemCheck/ItemCheckFactory";
import dayjs from 'dayjs';
import { IPageSettings } from "../../../interfaces/IPageSettings";
import { ComponentTypes } from "../../../config/config";
import { LanguageContext } from "../../../../../utilities/LocalizationModule";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { validSaveModes } from "../../../config/constants";
import { FolderTypeHelper } from "../../helpers/folderTypeHelper";
import { removeRecentItem } from "../../../redux/modules/recentItems"
import { removePinnedItem } from "../../../redux/modules/pinnedItems"
import savingLocationStructureModule from "../../../../../utilities/SavingLocationStructureModule";
import { FolderTypeEnum } from "../../enums/FolderTypeEnum";
import { setSavingLocation } from "../../../redux/modules/savingLocation";
import { mobileListItemStyle, isMobile } from "../../../styles/mobileStyles"
import { logger } from "../../../../../utilities/logger";
import generalModule from "../../../../../utilities/GeneralModule";
import { setIsContentManagerContentLoading } from "../../../redux/modules/contentManagerDirectory";
import { HomeFolderItem } from "../../_v2/components/Explorer/FolderView/HomeFolderItem";
import { IconTypeNameResolver } from "../../helpers/iconTypeNameResolver";
import { OutlookUtils } from "../../../../../utilities/OutlookUtils";
import { removeSuggestedItem } from "../../../redux/modules/suggestedItems";

const unsaveableFolderIconLocations = [FolderTypeIconEnum.teamMainSite, FolderTypeIconEnum.sharepointSite, FolderTypeIconEnum.oneDrive];

const mapDispatchToProps = {
  setLoading,
  setSavingLocation,
  setShowFolderDeleteDialog,
  removeRecentItem,
  removePinnedItem,
  removeSuggestedItem,
  setIsContentManagerContentLoading,
  setWorkingDirectories
};

const mapStateToProps = (state: RootState) => {
  return {
    user: state.user,
    hasFileSelected: state.files.files.some(file => file.isSelected),
    isTessEnabled: state.tenantInfo?.isTessEnabled
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface HomeItemProps extends PropsFromRedux {
  homeItem: IStoreItem;
  mode: ModeEnum;
  isRecentList: boolean;
  isSuggestedList?: boolean;
  openFileOptions: (item: IStoreItem, isRecentList: boolean) => void;
  openFolderOptions: (item: IStoreItem, isRecentList: boolean, isSuggestedList?: boolean) => void;
  showLicenseDialog: () => void;
  saveToOneDrive: (
    folderName: string,
    fromLocationItemCurrentDirectory?: IPageSettings,
    savingLocationDirectory?: string,
    locationBeforeSaving?: IPageSettings[]
  ) => Promise<void>;
  setLicenseMessageContent: (content: IDialogContentProps) => void;
}

const HomeItem: React.FunctionComponent<HomeItemProps> = ({
  homeItem,
  user,
  mode,
  isRecentList,
  isSuggestedList,
  hasFileSelected,
  isTessEnabled,
  openFileOptions,
  openFolderOptions,
  showLicenseDialog,
  saveToOneDrive,
  setShowFolderDeleteDialog,
  setLoading,
  setSavingLocation,
  removePinnedItem,
  removeRecentItem,
  removeSuggestedItem,
  setLicenseMessageContent,
  setIsContentManagerContentLoading,
  setWorkingDirectories
}: HomeItemProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const iconNameResolver = new IconTypeNameResolver(languageStrings);

  const CheckIfFolderExists = async (item: IStoreItem) => {
    const factory = new ItemCheckFactory(item.driveId, item.sharePointListId, item.siteId);
    const folderType = FolderTypeIconQuery.getFolderType(item.folderTypeIcon);
    const itemChecker = factory.createItemChecker(folderType, item?.isTeamsSiteInsideSharepoint);
    return await itemChecker.doesItemExist(item.itemId);
  };

  const IsFolderInValidState = async (item: IStoreItem, isRecentList: boolean) => {
    if (isTessEnabled && (item.tessAccountId || item.tessProjectId)) {
      return true;
    }

    let returnValue = true;
    try {

      let locationType = item.locations[0]?.type;

      if (!AccessLicenseHelper.canAccessItem(user.licenseType, locationType)) {
        const errorMessage = {
          type: DialogType.largeHeader,
          title: languageStrings.LicenseRequiredTitle,
          closeButtonAriaLabel: "Close",
          subText: languageStrings.LicenseRequired.replace("{placeholder}", locationType)
        };
        setLicenseMessageContent(errorMessage);
        showLicenseDialog();
        returnValue = false;
        return returnValue;
      }

      if (!user.settings.enableOneDrive || !user.settings.enableSharePoint || !user.settings.enableTeams) {
        if (!(locationType === ComponentTypes.OneDrive || locationType === ComponentTypes.OneDriveSubFolder)) {
          let isTeams = false;
          item.locations?.every(loc => {
            if (loc.teamGroupId || loc.isTeamsRootFolder || loc.isTeamsFolder) {
              isTeams = true;
            }
            return !isTeams;
          })
          locationType = isTeams ? ComponentTypes.Teams : locationType === ComponentTypes.SharePointList.toString() ? ComponentTypes.SharePointList : ComponentTypes.SharePoint;
        }
        const { canAccess, type } = AccessLicenseHelper.isLocationEnabled(locationType, user.settings);
        if (!canAccess) {
          const errorMessage = {
            type: DialogType.largeHeader,
            title: languageStrings.AccessDenied,
            closeButtonAriaLabel: "Close",
            subText: languageStrings.AccessRevoked.replace("{placeholder}", type)
          };
          setLicenseMessageContent(errorMessage);
          showLicenseDialog();
          returnValue = false;
          return returnValue;
        }
      }

      if (!(await CheckIfFolderExists(item))) {
        doFolderRemovalWork(isRecentList, item);
        returnValue = false;
      }
    }
    catch (e) {
      logger(e, "HomeItem.tsx (isFolderInValidState)");
    }

    return returnValue;
  };

  const doFolderRemovalWork = async (isRecentList: boolean, item: IStoreItem) => {
    const itemToRemove = {
      itemId: item.itemId,
      driveId: item.driveId
    };

    if (isSuggestedList) {
      try {
        const sender: string = mode !== ModeEnum.mailCompose
          ? Office?.context?.mailbox?.item?.from?.emailAddress ?? user.account.username
          : (await OutlookUtils.getItemFrom()) ?? user.account.username;

        removeSuggestedItem(user.account.accountId, sender, itemToRemove);
      } catch (error) {
        logger(error, "HomeItem.tsx (removeSuggestedItem)");
      }
    }
    else {
      isRecentList
        ? removeRecentItem(user.account.accountId, itemToRemove, FileTypeEnum.folder)
        : removePinnedItem(user.account.accountId, itemToRemove, FileTypeEnum.folder);
    }

    setShowFolderDeleteDialog(true);
  };

  const getPath = (workingDirectories: IPageSettings[], folderTypeIcon: FolderTypeIconEnum, includeBrackets = true): string => {
    const type = iconNameResolver.getIconTypeName(folderTypeIcon);
    const path = workingDirectories
      .filter(dir => dir.type !== ComponentTypes.Saving)
      .map(dir => dir?.displayName)
      .join("/");
    return includeBrackets ? `[${type}] ${path}` : path;
  };

  const save = async (storeItem: IStoreItem) => {
    setContextAwareLoading(true);
    try {

      savingLocationStructureModule.setSavingLocationStructure(storeItem.locations);

      const folderType = FolderTypeHelper.getType(storeItem.locations);

      if (folderType == FolderTypeEnum.oneDrive || folderType == FolderTypeEnum.oneDriveSubFolder) {
        const destinationLocation = storeItem.locations[storeItem.locations.length - 1];
        setSavingLocation(
          {
            display: destinationLocation.displayName,
            webUrl: destinationLocation.webUrl,
            directory: destinationLocation.directory
          },
          true
        );
      }

      const savingDirectory = {
        ...storeItem.locations[storeItem.locations.length - 1],
        isSaveLocationStructureSet: true,
        displayName: languageStrings.Properties,
        type: ComponentTypes.Saving
      }

      const newLocations = [
        ...storeItem.locations,
        savingDirectory,
      ];

      setContextAwareLoading(false);
      setWorkingDirectories(newLocations);
    } catch (error) {
      setContextAwareLoading(false);
      logger(error, "homeItem.tsx (save)");
    }
  };

  const setContextAwareLoading = React.useCallback((isLoading: boolean, contentManagerOnly: boolean = false) => {
    if (!contentManagerOnly) {
      !generalModule.isInConentMangerMode() ? setLoading(isLoading) : setIsContentManagerContentLoading(isLoading);
    }
    else {
      setIsContentManagerContentLoading(isLoading);
    }
  }, []);

  const isValidDropZone = React.useMemo((): boolean => {
    return (!unsaveableFolderIconLocations.some(icon => homeItem.folderTypeIcon === icon) && validSaveModes.some(item => item === mode) || (!unsaveableFolderIconLocations.some(icon => homeItem.folderTypeIcon === icon) && generalModule.isInConentMangerMode()));
  }, [homeItem, mode]);

  return (
    homeItem.fileType === FileTypeEnum.folder
      ? <HomeFolderItem
        homeItem={homeItem}
        isMobile={isMobile()}
        isRecentList={isRecentList}
        isSuggestedList={isSuggestedList}
        hasFileSelected={hasFileSelected}
        isValidDropZone={isValidDropZone}
        setContextAwareLoading={setContextAwareLoading}
        IsFolderInValidState={IsFolderInValidState}
        getPath={getPath}
        save={save}
        openFolderOptions={openFolderOptions} />
      : (
        <div className="list-item" style={isMobile() ? mobileListItemStyle : {}}>
          <div className="list-item-link" onClick={() => openFileOptions(homeItem, isRecentList)}>
            <TooltipHost content={homeItem.filePath}>
              <div className="list-item-icon">
                <Icon
                  {...getFileTypeIconProps({
                    extension: homeItem.extension,
                    size: 20,
                    imageFileType: "svg"
                  })}
                />
              </div>
            </TooltipHost>
            <Text className="list-item-name">
              <TooltipHost content={homeItem.name} overflowMode={TooltipOverflowMode.Parent}>
                {homeItem.name}
              </TooltipHost>
              <div className="xb-grey-text">
                {dayjs(homeItem.lastModifiedDateTime).format("l")} {languageStrings.By} {homeItem.modifiedBy ?? languageStrings.Unknown}
              </div>
            </Text>
          </div>
          <TooltipHost content={languageStrings.MoreActions}>
            <div className={isMobile() ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
              <FontIcon
                iconName="More"
                onClick={() => openFileOptions(homeItem, isRecentList)}
              />
            </div>
          </TooltipHost>
        </div>
      )
  );
};

export default connector(HomeItem);
